import { useCallback, useEffect, useMemo } from "react";
import {
  Col,
  Form,
  Input,
  JsonEditor,
  Row,
  Textarea,
  type FormInstance,
  type JsonValue,
} from "@ir/ui";
import type { PurchaserConfigTemplateFormValues } from "./purchaserConfigTemplateFormUtils";

interface PurchaserConfigTemplateFormProps {
  form: FormInstance<PurchaserConfigTemplateFormValues>;
  initialValues: PurchaserConfigTemplateFormValues;
  onSubmit: (values: PurchaserConfigTemplateFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

const JSON_FIELD_RULE = {
  validator: async (_: unknown, value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return;
    try {
      JSON.parse(trimmed);
    } catch {
      throw new TypeError("Must be valid JSON");
    }
  },
};

const NUMBER_FIELD_RULE = {
  validator: async (_: unknown, value: string | undefined) => {
    const trimmed = (value ?? "").trim();
    if (!trimmed) return;
    if (!Number.isFinite(Number(trimmed))) {
      throw new TypeError("Must be a valid number");
    }
  },
};

function parseStoredJson(value: string | undefined): JsonValue {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (parsed != null && typeof parsed === "object") {
      return parsed as JsonValue;
    }
    return {};
  } catch {
    return {};
  }
}

interface JsonEditorFieldProps {
  value?: string;
  onChange?: (next: string) => void;
  rootName: string;
}

const JsonEditorField = ({
  value,
  onChange,
  rootName,
}: JsonEditorFieldProps) => {
  return (
    <JsonEditor
      value={parseStoredJson(value)}
      onChange={(next) => onChange?.(JSON.stringify(next, null, 2))}
      rootName={rootName}
      collapse={2}
    />
  );
};

export const PurchaserConfigTemplateForm = ({
  form,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: PurchaserConfigTemplateFormProps) => {
  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const isDirty =
      (values.metadata ?? "").trim() !==
        (initialValues.metadata ?? "").trim() ||
      (values.searchKeywords ?? "").trim() !==
        (initialValues.searchKeywords ?? "").trim() ||
      (values.intelligenceFilter ?? "").trim() !==
        (initialValues.intelligenceFilter ?? "").trim() ||
      (values.deductionTypeInvoicePatternForDiscount ?? "").trim() !==
        (initialValues.deductionTypeInvoicePatternForDiscount ?? "").trim() ||
      (values.additionalFieldMapping ?? "").trim() !==
        (initialValues.additionalFieldMapping ?? "").trim() ||
      (values.discountTermAgreementDaysTolerance ?? "").trim() !==
        (initialValues.discountTermAgreementDaysTolerance ?? "").trim();

    onFormValidityChange?.(!hasErrors && isDirty);
  }, [form, initialValues, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const jsonRules = useMemo(() => [JSON_FIELD_RULE], []);
  const numberRules = useMemo(() => [NUMBER_FIELD_RULE], []);

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onSubmit}
      onFieldsChange={updateFormValidity}
      onValuesChange={updateFormValidity}
      className="purchaser-config-template-form"
    >
      <Row>
        <Col span={6}>
          <Form.Item
            label="Metadata (JSON)"
            name="metadata"
            validateTrigger={["onChange", "onBlur"]}
            rules={jsonRules}
          >
            <JsonEditorField rootName="metadata" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Search Keywords"
            name="searchKeywords"
            validateTrigger={["onChange", "onBlur"]}
          >
            <Textarea placeholder="Enter search keywords" maxLength={255} minRows={7} />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={6}>
          <Form.Item
            label="Intelligence Filter (JSON)"
            name="intelligenceFilter"
            validateTrigger={["onChange", "onBlur"]}
            rules={jsonRules}
          >
            <JsonEditorField rootName="intelligenceFilter" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Deduction Type Invoice Pattern for Discount (JSON)"
            name="deductionTypeInvoicePatternForDiscount"
            validateTrigger={["onChange", "onBlur"]}
            rules={jsonRules}
          >
            <JsonEditorField rootName="deductionTypeInvoicePatternForDiscount" />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={6}>
          <Form.Item
            label="Additional Field Mapping (JSON)"
            name="additionalFieldMapping"
            validateTrigger={["onChange", "onBlur"]}
            rules={jsonRules}
          >
            <JsonEditorField rootName="additionalFieldMapping" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Discount Term Agreement Days Tolerance"
            name="discountTermAgreementDaysTolerance"
            validateTrigger={["onChange", "onBlur"]}
            rules={numberRules}
          >
            <Input placeholder="e.g. 2" inputMode="decimal" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
